import {
	Breakpoints,
	Container,
	ContainerGutter,
	ContainerPadding,
	CTA,
	Spinner,
	Text,
	TextAlignment,
	TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import React from 'react';
import { useCitaHandler } from '../../hooks/use-cita-handler';
import { useHuchaPdfGenerator } from '../../../shared/hooks/use-hucha-pdf-generator';
import { HuchaVentaValues } from '../../../shared/models/hucha-values';
import { HuchaDisclaimer1Content } from '../disclaimers/hucha-disclaimer1-content';
import { HuchaDisclaimer210To15Content } from '../disclaimers/hucha-disclaimer2-10-to-15-content';
import { PopupHuchaRenovadoresCard } from './popup-card/popup-hucha-renovadores-card/popup-hucha-renovadores-card';
import { PdfHuchaContent } from '../../../shared/components/pdf-hucha-content';
import { useHuchaVentaLayerContent } from '../../hooks/use-hucha-venta-layer-content';

export const Renovador10To15VouchersPopup: React.FC<{
	huchaValues: HuchaVentaValues;
}> = ({ huchaValues }) => {
	const dineroHucha = huchaValues?.dineroHucha ?? '0';
	const dineroRenovadores = huchaValues?.dineroRenovadores ?? '0';
	const name = huchaValues?.name || '';

	const targetRefPDF = React.useRef<HTMLDivElement>(null);
	const { handlePdfDownloadClick, isDownloading, isPdfReady } = useHuchaPdfGenerator(huchaValues, targetRefPDF);
	const { handleCita } = useCitaHandler(huchaValues?.huchaTier ?? 'renovador10To15');
	const { huchaVentaPdfLayerContent } = useHuchaVentaLayerContent(huchaValues);

	return (
		<>
			<Container
				padding={{
					right: {
						[Breakpoints.default]: ContainerPadding.grid001,
						[Breakpoints.b560]: ContainerPadding.grid002,
						[Breakpoints.b960]: ContainerPadding.grid001,
					},
					bottom: ContainerPadding.dynamic0200,
					left: {
						[Breakpoints.default]: ContainerPadding.grid001,
						[Breakpoints.b560]: ContainerPadding.grid002,
						[Breakpoints.b960]: ContainerPadding.grid001,
					},
				}}
				horizontalAlign={'center'}
				wrap={'always'}
			>
				<PopupHuchaRenovadoresCard dineroHucha={dineroHucha} />

				<Container
					wrap={'always'}
					padding={{
						top: ContainerPadding.dynamic0130,
					}}
				>
					<Container
						horizontalAlign={'center'}
						verticalAlign={'center'}
						wrap={'always'}
						gutter={ContainerGutter.static400}
					>
						<Text appearance={TokenTextAppearance.copy200} textAlign={TextAlignment.center}>
							{name}, por formar parte del club,{' '}
							<Text bold>
								acumulas 10€ en tu hucha mes a mes<Text sup>1</Text>.
							</Text>
						</Text>

						<Text appearance={TokenTextAppearance.copy200} textAlign={TextAlignment.center}>
							Y ahora, para celebrar todos estos años conduciendo un Volkswagen, queremos darte la Excusa Oficial
							Volkswagen:
							<Text bold>
								{' '}
								{dineroRenovadores}€<Text sup>2</Text> adicionales que se sumarán al saldo de tu hucha para renovar tu
								coche.
							</Text>
						</Text>
					</Container>

					<Container
						padding={{
							top: {
								[Breakpoints.default]: ContainerPadding.dynamic0150,
								[Breakpoints.b960]: ContainerPadding.dynamic0140,
							},
						}}
						verticalAlign={'center'}
						horizontalAlign={'center'}
					>
						<CTA
							tag="button"
							aria-label="Descargar ahorro acumulado"
							size="large"
							onClick={handlePdfDownloadClick}
							emphasis="primary"
							disabled={isDownloading}
							stretchContent={{ [Breakpoints.default]: true, [Breakpoints.b560]: false }}
							icon={isDownloading ? <Spinner /> : undefined}
						>
							<Text bold>{isDownloading ? 'Descargando' : 'Descargar ahorro acumulado'}</Text>
						</CTA>
					</Container>
					<Container
						wrap={'always'}
						padding={{
							top: {
								[Breakpoints.default]: ContainerPadding.dynamic0120,
								[Breakpoints.b960]: ContainerPadding.dynamic0100,
							},
						}}
						verticalAlign={'center'}
						horizontalAlign={'center'}
						gutter={ContainerGutter.dynamic0120}
					>
						<Text appearance={TokenTextAppearance.headline300} textAlign={TextAlignment.center} bold>
							¿Cómo puedes utilizarlo?
						</Text>
						<Text appearance={TokenTextAppearance.copy200} textAlign={TextAlignment.center}>
							Muy sencillo. Elige tu nuevo Volkswagen, descarga tu ahorro acumulado y muéstralo en un concesionario para
							poder disfrutar tanto de la cantidad acumulada en tu hucha, como de los {dineroRenovadores}€ adicionales
							de Excusa Oficial Volkswagen.
						</Text>
						<Text appearance={TokenTextAppearance.copy200} textAlign={TextAlignment.center} bold>
							Recuerda: tienes hasta el 31 de diciembre para hacer uso del descuento especial de {dineroRenovadores}€.
						</Text>
					</Container>
					<Container
						padding={{
							top: ContainerPadding.dynamic0150,
						}}
						verticalAlign={'center'}
						horizontalAlign={'center'}
					>
						<CTA
							tag="button"
							aria-label="Pide cita en tu concesionario"
							size="large"
							onClick={handleCita}
							emphasis="nbab"
						>
							<Text bold>Pide cita en tu concesionario</Text>
						</CTA>
					</Container>

					<Container
						wrap={'always'}
						padding={{
							top: ContainerPadding.dynamic0150,
						}}
						verticalAlign={'center'}
						horizontalAlign={'center'}
						gutter={ContainerGutter.dynamic0100}
					>
						<Text appearance={TokenTextAppearance.copy200} textAlign={TextAlignment.center}>
							Si tienes cualquier duda sobre el funcionamiento de tu Hucha Volkswagen, llámanos al 800 500 100 o
							escríbenos a{' '}
							<CTA tag="a" emphasis="tertiary" href="mailto:atencioncliente@volkswagen.es">
								<Text appearance={TokenTextAppearance.copy200}>atencioncliente@volkswagen.es</Text>
							</CTA>
							. Estaremos encantados de ayudarte.
						</Text>
					</Container>

					<Container
						wrap={'always'}
						padding={{
							top: {
								[Breakpoints.default]: ContainerPadding.dynamic0200,
								[Breakpoints.b560]: ContainerPadding.dynamic0150,
							},
						}}
						verticalAlign={'center'}
						horizontalAlign={'center'}
						gutter={ContainerGutter.static350}
					>
						<HuchaDisclaimer1Content />
						<HuchaDisclaimer210To15Content />
					</Container>
				</Container>
			</Container>
			{isPdfReady && <PdfHuchaContent targetRef={targetRefPDF} layerContent={() => huchaVentaPdfLayerContent} />}
		</>
	);
};
